import React, { useState } from 'react'
import { Table, Button, Icon, Tooltip } from 'antd'
import moment from 'moment'
import { Map, CircleMarker, Popup, TileLayer, GeoJSON, Marker, Polyline } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { geojsonIndia } from '../../util/methods'
import GeoJSONLabels from '../../components/GeoJSONLabels/GeoJSONLabels'
import { getLeafletTileConfig } from '../../util/mapTiles'

function parseLoc(value) {
    if (!value || typeof value !== 'string') return null
    const parts = value.split(',')
    if (parts.length !== 2) return null
    const lat = Number(parts[0])
    const lng = Number(parts[1])
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
    return [lat, lng]
}

function distanceMeters(record) {
    const start = Number(record && record.start_odometer) || 0
    const end = Number(record && record.end_odometer) || 0
    const diff = end - start
    return diff > 0 ? diff : 0
}

function hms(ms) {
    const totalSeconds = Math.floor(Number(ms || 0) / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

const startIcon = L.icon({
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
})

function IgnitionTimeModal({ ignitionTimeData = [] }) {
    const tileLayerConfig = getLeafletTileConfig()
    const [isMapView, setIsMapView] = useState(false)
    const [selectedRecord, setSelectedRecord] = useState(null)
    const [zoom, setZoom] = useState(15)

    const handleZoomChange = () => {
        const mapElement = document.querySelector('.leaflet-container')
        if (mapElement && mapElement._leaflet_map) {
            setZoom(mapElement._leaflet_map.getZoom())
        }
    }

    const focusOn = (record) => {
        setSelectedRecord(record)
        setIsMapView(true)
    }

    const locationCell = (locStr, record) => {
        const parsed = parseLoc(locStr)
        if (!parsed) return '-'
        return (
            <Tooltip title={locStr}>
                <Icon
                    type="environment"
                    style={{ fontSize: '16px', cursor: 'pointer', color: '#1890ff' }}
                    onClick={() => focusOn(record)}
                />
            </Tooltip>
        )
    }

    const columns = [
        {
            title: 'Start Time',
            dataIndex: 'startTime',
            key: 'startTime',
            sorter: (a, b) => moment(a.startTime).unix() - moment(b.startTime).unix(),
            render: (text) => moment(text).format('DD/MM/YYYY HH:mm:ss'),
        },
        {
            title: 'End Time',
            dataIndex: 'endTime',
            key: 'endTime',
            sorter: (a, b) => moment(a.endTime).unix() - moment(b.endTime).unix(),
            render: (text) => moment(text).format('DD/MM/YYYY HH:mm:ss'),
        },
        {
            title: 'Duration',
            dataIndex: 'duration',
            key: 'duration',
            sorter: (a, b) => a.duration - b.duration,
            render: (text) => hms(text),
        },
        {
            title: 'Distance',
            key: 'distance',
            sorter: (a, b) => distanceMeters(a) - distanceMeters(b),
            render: (text, record) => `${(distanceMeters(record) / 1000).toFixed(2)} km`,
        },
        {
            title: 'Start Location',
            dataIndex: 'start_location',
            key: 'start_location',
            render: (text, record) => locationCell(text, record),
        },
        {
            title: 'End Location',
            dataIndex: 'end_location',
            key: 'end_location',
            render: (text, record) => locationCell(text, record),
        },
        {
            title: 'Start Fuel Level',
            dataIndex: 'start_fuel_level',
            key: 'start_fuel_level',
            sorter: (a, b) => a.start_fuel_level - b.start_fuel_level,
            render: (text) => `${text} Ltrs`,
        },
        {
            title: 'End Fuel Level',
            dataIndex: 'end_fuel_level',
            key: 'end_fuel_level',
            sorter: (a, b) => a.end_fuel_level - b.end_fuel_level,
            render: (text) => `${text} Ltrs`,
        },
        {
            title: 'Fuel Consumed',
            dataIndex: 'fuel_consumed',
            key: 'fuel_consumed',
            sorter: (a, b) => a.fuel_consumed - b.fuel_consumed,
            render: (text) => `${text} Ltrs`,
        },
        {
            title: 'Fuel Consumption Rate',
            dataIndex: 'fuel_consumed_rate_hr',
            key: 'fuel_consumed_rate_hr',
            sorter: (a, b) => a.fuel_consumed_rate_hr - b.fuel_consumed_rate_hr,
            render: (text) => `${text} Ltrs/Hr`,
        },
    ]

    const recordsForMap = selectedRecord ? [selectedRecord] : ignitionTimeData
    const mapCenter = (() => {
        const first = recordsForMap.find((r) => parseLoc(r.start_location) || parseLoc(r.end_location))
        return (first && (parseLoc(first.start_location) || parseLoc(first.end_location))) || [20.5937, 78.9629]
    })()

    // When a single record is focused, fit the map to include BOTH its start and
    // end locations so the whole trip segment is visible at once.
    const selectedBounds = (() => {
        if (!selectedRecord) return null
        const pts = [parseLoc(selectedRecord.start_location), parseLoc(selectedRecord.end_location)].filter(Boolean)
        if (!pts.length) return null
        return L.latLngBounds(pts).pad(0.4)
    })()

    const mapPositionProps = selectedBounds
        ? { bounds: selectedBounds }
        : { center: mapCenter, zoom: 10 }

    const renderMap = () => (
        <div style={{ height: '600px' }}>
            <Map
                {...mapPositionProps}
                maxZoom={tileLayerConfig.maxZoom || 18}
                attributionControl={false}
                style={{ height: '100%' }}
                onZoomend={handleZoomChange}
            >
                <TileLayer url={tileLayerConfig.url} subdomains={tileLayerConfig.subdomains} />
                <GeoJSON data={geojsonIndia} style={{ color: 'grey', weight: 1 }} />
                <GeoJSONLabels zoom={zoom} minZoomLevel={17} />

                {recordsForMap.map((item) => {
                    const start = parseLoc(item.start_location)
                    const end = parseLoc(item.end_location)
                    const key = `${item.startTime}-${item.endTime}`
                    const popup = (
                        <Popup>
                            <div>
                                <strong>Duration: </strong>{hms(item.duration)}<br />
                                <strong>Start: </strong>{moment(item.startTime).format('DD/MM/YYYY HH:mm:ss')}<br />
                                <strong>End: </strong>{moment(item.endTime).format('DD/MM/YYYY HH:mm:ss')}<br />
                                <strong>Start Fuel: </strong>{item.start_fuel_level} Ltrs<br />
                                <strong>End Fuel: </strong>{item.end_fuel_level} Ltrs
                            </div>
                        </Popup>
                    )
                    return (
                        <React.Fragment key={key}>
                            {start && end && (
                                <Polyline positions={[start, end]} color="#1890ff" weight={2} dashArray="4 4" />
                            )}
                            {start && (
                                <Marker position={start} icon={startIcon}>{popup}</Marker>
                            )}
                            {end && (
                                <CircleMarker center={end} radius={7} fillColor="#EE3711" fillOpacity={0.8} color="#B34029" weight={1}>
                                    {popup}
                                </CircleMarker>
                            )}
                        </React.Fragment>
                    )
                })}
            </Map>
            <div style={{ marginTop: 6, fontSize: 12 }}>
                <Icon type="environment" style={{ color: '#1890ff' }} /> Start&nbsp;&nbsp;
                <span style={{ color: '#EE3711' }}>●</span> End
            </div>
        </div>
    )

    const renderTable = () => (
        <Table
            dataSource={[...ignitionTimeData].sort((a, b) => b.duration - a.duration)}
            columns={columns}
            pagination={{ pageSize: 10 }}
            rowKey={(record) => `${record.startTime}-${record.endTime}`}
            scroll={{ x: true }}
            size="small"
        />
    )

    const calculateTotalDuration = () => {
        const totalMs = ignitionTimeData.reduce((sum, item) => sum + Number(item.duration || 0), 0)
        return `Total Duration: ${hms(totalMs)}`
    }

    return (
        <>
            {isMapView ? renderMap() : renderTable()}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <Button
                    style={{ marginTop: '10px' }}
                    type="primary"
                    onClick={() => {
                        setIsMapView(!isMapView)
                        setSelectedRecord(null)
                    }}
                    size="small"
                >
                    Switch to {isMapView ? 'Table' : 'Map'} View
                </Button>
                <div style={{ fontWeight: 'bold' }}>
                    {calculateTotalDuration()}
                </div>
            </div>
        </>
    )
}

export default IgnitionTimeModal
